import crypto from "crypto";
import z from "zod";

const GET_CREDENTIALS_URL = process.env.BUILD0_CREDENTIALS_URL!;
const ENCRYPTION_KEY = process.env.BUILD0_ENCRYPTION_KEY!;
const CREDENTIALS_AUTH_KEY = "app-credentials";
const ALGORITHM = "aes-256-gcm";

/** Base credential structure from Build0 proxy */
const CredentialSchema = z.record(
  z.string().describe("Integration name"),
  z
    .object({
      type: z.string().describe("Credential type"),
      provider: z.string().describe("Credential provider"),
    })
    .passthrough()
);

type Credentials = z.infer<typeof CredentialSchema>;

/** Error thrown when credentials cannot be retrieved */
class CredentialError extends Error {
  constructor(message: string, public cause?: Error) {
    super(message);
    this.name = "CredentialError";
  }
}

function decryptCredentials(encryptedData: string): any {
  const parts = encryptedData.split(":");
  if (parts.length !== 3) {
    throw new Error("Invalid encrypted data format");
  }

  const [ivHex, authTagHex, encrypted] = parts;
  const key = Buffer.from(ENCRYPTION_KEY, "hex");
  const iv = Buffer.from(ivHex, "hex");
  const authTag = Buffer.from(authTagHex, "hex");

  const decipher = crypto.createDecipheriv(ALGORITHM, key, iv);
  decipher.setAuthTag(authTag);
  decipher.setAAD(Buffer.from(CREDENTIALS_AUTH_KEY));

  let decrypted = decipher.update(encrypted, "hex", "utf8");
  decrypted += decipher.final("utf8");

  return JSON.parse(decrypted);
}

/**
 * Retrieves and validates credentials from environment variable or credential endpoint
 * @param request - Request object
 * @returns Promise resolving to validated credentials object
 * @throws {CredentialError} When credentials cannot be retrieved or are invalid
 */
export async function getCredentials(request: Request): Promise<Credentials> {
  try {
    // Get cookies from the request to forward for authentication
    const cookieHeader = request.headers.get("cookie");

    // Fetch credentials from endpoint with forwarded cookies
    const response = await fetch(GET_CREDENTIALS_URL, {
      headers: {
        ...(cookieHeader && { Cookie: cookieHeader }),
      },
    });

    if (!response.ok) {
      throw new CredentialError(
        `Failed to fetch credentials: ${response.status} ${response.statusText}`
      );
    }

    const data = (await response.json()) as { encrypted: string };

    // Decrypt the credentials
    const decryptedData = decryptCredentials(data.encrypted);

    // Validate credentials structure
    const credentials = CredentialSchema.parse(decryptedData);

    return credentials;
  } catch (error) {
    if (error instanceof CredentialError) {
      throw error;
    }

    throw new CredentialError(
      "Failed to retrieve credentials from environment variable or endpoint",
      error as Error
    );
  }
}

export { CredentialError, type Credentials };
